/**
 * =============================================================================
 * SCRIPT-NAME:   gsc_status.js
 * PROJEKT:       Freiseinapp - GSC KPI-Dashboard
 * MODUL:         Per-Seite-Statusansicht — wo steht jede LP/LB, Rank & Probleme
 * BESCHREIBUNG:  Liest gsc_get_status.php (Index-Status + Tages-Rank + belastbare
 *                Problem-Flags) und zeigt es als Tabelle, Probleme zuerst.
 * VERSION:       v1.0 · DATUM: 2026-07-08
 * =============================================================================
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import "./gsc_status.css";

const API_BASE = "https://freiseinapp.de/api";
const PILLAR_COLORS = { VA:'#dc2626', EF:'#0ea5e9', EA:'#16a34a', SO:'#9333ea', PBC:'#d97706' };

// Letzter Crawl als Zahl. Seiten ohne erfassten Crawl gelten als "unendlich lange
// nicht gesehen" und stehen deshalb bei "älteste zuerst" ganz oben — das ist der
// Extremfall der Frage, nicht ihr Ausreißer.
const crawlZeit = p => {
    const c = (p.crawls || [])[0];
    return c ? new Date(String(c.crawl_time).replace(' ', 'T')).getTime() : -Infinity;
};

// Sortier-Definition je Spalte. `erst` ist die Richtung des ersten Klicks —
// jeweils die, die das Interessante nach oben holt.
const SPALTEN = {
    prefix: { label: 'Seite',     wert: p => p.prefix || '',                              erst: 'asc'  },
    spark:  { label: 'Impr. 30T', wert: p => (p.spark || []).reduce((a, b) => a + b, 0),  erst: 'desc' },
    pillar: { label: 'Pillar',    wert: p => p.pillar || '',                              erst: 'asc'  },
    index:  { label: 'Index',     wert: p => (p.is_indexed === null ? -1 : p.is_indexed), erst: 'asc'  },
    crawl:  { label: 'Crawl',     wert: crawlZeit,                                        erst: 'asc'  },
    rank:   { label: 'Rank',      wert: p => (p.rank === null ? Infinity : p.rank),       erst: 'asc'  },
    impr:   { label: 'Impr',      wert: p => p.impressions || 0,                          erst: 'desc' },
    klicks: { label: 'Klicks',    wert: p => p.clicks || 0,                               erst: 'desc' },
    notes:  { label: 'Notizen',   wert: p => (p.notes || []).length,                      erst: 'desc' },
};

function IndexBadge({ p }) {
    if (p.is_indexed === 1) return <span className="gs-badge gs-b-ok">✅ indexiert</span>;
    if (p.is_indexed === null) return <span className="gs-badge gs-b-warn">– n/a</span>;
    const unknown = (p.coverage_state || '').includes('nicht bekannt');
    return <span className="gs-badge gs-b-crit">{unknown ? '🔴 unbekannt' : '🔴 nicht indexiert'}</span>;
}

// Mini-Sparkline: Impressionen je Tag der ROLLIERENDEN letzten 30 Tage (values[0]=ältester Tag).
function Sparkline({ values }) {
    const W = 96, H = 24, P = 2;
    if (!Array.isArray(values) || values.length === 0) return <span style={{ color: '#c4c4c4' }}>–</span>;
    const total = values.reduce((a, b) => a + b, 0);
    if (total === 0) return <span style={{ color: '#c4c4c4' }} title="0 Impressionen in 30 Tagen">–</span>;
    const peak = Math.max(...values);
    const n = values.length;
    const dx = n > 1 ? (W - 2 * P) / (n - 1) : 0;
    const yOf = v => H - P - (peak > 0 ? (v / peak) * (H - 2 * P) : 0);
    const pts = values.map((v, i) => `${(P + i * dx).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ');
    const li = n - 1;
    return (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}
             role="img" aria-label={`Impressionen 30 Tage: Summe ${total}, Spitze ${peak}`}>
            <title>{`Impressionen letzte 30 Tage — Summe ${total}, Spitze ${peak}/Tag`}</title>
            <polyline points={pts} fill="none" stroke="#1a7f5a" strokeWidth="1.5" />
            <circle cx={(P + li * dx).toFixed(1)} cy={yOf(values[li]).toFixed(1)} r="2" fill="#1a7f5a" />
        </svg>
    );
}

// "2026-07-09 10:15:32" → "09.07. 10:15"
function fmtTs(ts) {
    if (!ts) return '';
    const [d, t] = String(ts).split(' ');
    if (!d) return String(ts);
    const [, M, D] = d.split('-');
    return `${D}.${M}. ${(t || '').slice(0, 5)}`;
}

// Datum ohne Uhrzeit: "21.08."
function fmtTag(ts) {
    if (!ts) return '';
    const [, M, D] = String(ts).split(' ')[0].split('-');
    return `${D}.${M}.`;
}

// Volles Datum mit Uhrzeit: "21.08.2026, 09:16"
function fmtVoll(ts) {
    if (!ts) return '';
    const [d, t] = String(ts).split(' ');
    const [Y, M, D] = d.split('-');
    return `${D}.${M}.${Y}, ${(t || '').slice(0, 5)}`;
}

function tageSeit(ts) {
    if (!ts) return null;
    const then = new Date(String(ts).replace(' ', 'T'));
    return Math.floor((Date.now() - then.getTime()) / 86400000);
}

// Crawl-Verlauf je Seite (gsc_crawl_history, ab 2026-08-22).
// gsc_url_inspection kennt nur den LETZTEN Crawl — hier steht jeder Zeitpunkt.
// Die Alters-Ampel ist der eigentliche Nutzwert: sie zeigt auf einen Blick,
// welche Seiten Google seit Wochen nicht mehr angesehen hat.
function CrawlHistory({ page }) {
    const [open, setOpen] = useState(false);
    const crawls = page.crawls || [];
    const n = page.crawl_count || 0;
    if (!n || !crawls.length) return <span className="gs-crawl-none" title="Noch kein Crawl erfasst">–</span>;

    const letzter = crawls[0];
    const alter = tageSeit(letzter.crawl_time);
    const ampel = alter === null ? '' : alter <= 7 ? 'frisch' : alter <= 30 ? 'mittel' : 'alt';
    // Verweisende Seiten laut Google zum Crawl-Zeitpunkt. 0 ist das eigentliche
    // Signal: eine Seite, auf die nichts zeigt, hat Google keinen Anlass zu gewichten.
    // ⚠ Laut Google eine Stichprobe, keine vollständige Liste.
    const refs = letzter.refs;

    return (
        <div className="gs-crawl">
            <button className={`gs-crawl-btn gs-crawl-${ampel}`} onClick={() => setOpen(!open)}
                title={`${fmtVoll(letzter.crawl_time)} · ${n} erfasst`}>
                <span className="gs-crawl-date">{fmtTag(letzter.crawl_time)}</span>
                {alter !== null && <span className="gs-crawl-age">{alter}T</span>}
                {refs !== null && refs !== undefined && (
                    <span className={`gs-crawl-refs${refs === 0 ? ' gs-crawl-refs-null' : ''}`}
                        title={refs === 0
                            ? 'Keine verweisende Seite — Google kennt keinen internen Link hierher'
                            : `${refs} verweisende Seite${refs === 1 ? '' : 'n'} laut Google (Stichprobe)`}>
                        🔗{refs}
                    </span>
                )}
                {n > 1 && <span className="gs-crawl-n">{n}×</span>}
                <span className="gs-crawl-caret">{open ? '▾' : '▸'}</span>
            </button>
            {open && (
                <ul className="gs-crawl-list">
                    {crawls.map(c => (
                        <li key={c.crawl_time}>
                            <span className="gs-crawl-ts">{fmtVoll(c.crawl_time)}</span>
                            <span className="gs-crawl-as" title={c.crawled_as || 'unbekannt'}>
                                {c.crawled_as === 'MOBILE' ? '📱' : c.crawled_as ? '🖥' : '·'}
                            </span>
                            {c.refs !== null && c.refs !== undefined && (
                                <span className={`gs-crawl-refs${c.refs === 0 ? ' gs-crawl-refs-null' : ''}`}>🔗{c.refs}</span>
                            )}
                            <span className={c.is_indexed ? 'gs-crawl-ok' : 'gs-crawl-bad'}>
                                {c.coverage_state || '–'}
                            </span>
                        </li>
                    ))}
                    {n > crawls.length && (
                        <li className="gs-crawl-more">… {n - crawls.length} ältere nicht angezeigt</li>
                    )}
                </ul>
            )}
        </div>
    );
}

// Notiz-Verlauf je Seite: datierte Einträge (neueste oben) + Eingabebox.
// Bei Problem-Seiten ist die Box mit dem roten Problemtext vorbefüllt (source='problem').
function NotesLog({ page, token }) {
    const problemText = (page.problems || []).join(' · ');
    const [notes, setNotes] = useState(page.notes || []);
    const [draft, setDraft] = useState((page.notes && page.notes.length) ? '' : problemText);
    const [busy, setBusy] = useState(false);

    async function add() {
        const text = draft.trim();
        if (!text || busy) return;
        setBusy(true);
        const source = (text === problemText.trim() && problemText) ? 'problem' : 'user';
        try {
            const res = await fetch(`${API_BASE}/gsc_notes_log.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: page.url, prefix: page.prefix, note: text, source }),
            });
            const j = await res.json();
            if (j.success) { setNotes([{ id: j.id, note: text, source: j.source || source, created_at: j.created_at }, ...notes]); setDraft(''); }
        } catch (e) { console.error('Notiz speichern:', e); }
        setBusy(false);
    }

    async function del(id) {
        try {
            const res = await fetch(`${API_BASE}/gsc_notes_log.php?id=${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            const j = await res.json();
            if (j.success) setNotes(notes.filter(n => n.id !== id));
        } catch (e) { console.error('Notiz löschen:', e); }
    }

    return (
        <div className="gs-notes">
            <div className="gs-notes-input">
                <textarea value={draft} rows={2} placeholder="Notiz…" className="gs-notes-ta"
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); add(); } }} />
                <button className="gs-notes-add" onClick={add} disabled={busy || !draft.trim()} title="Notiz speichern (⌘/Strg+Enter)">＋</button>
            </div>
            {notes.length > 0 && (
                <ul className="gs-notes-list">
                    {notes.map(n => (
                        <li key={n.id} className={n.source === 'problem' ? 'gs-note-problem' : ''}>
                            <span className="gs-note-ts">{fmtTs(n.created_at)}</span>
                            <span className="gs-note-txt">{n.source === 'problem' ? '🔴 ' : ''}{n.note}</span>
                            <button className="gs-note-del" onClick={() => del(n.id)} title="Eintrag löschen">×</button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function GscStatus() {
    const token = localStorage.getItem("token");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [onlyProblems, setOnlyProblems] = useState(false);
    const [days, setDays] = useState(7);
    const [sort, setSort] = useState(null);   // null = Vorgabe | { key, dir }

    useEffect(() => {
        let alive = true;
        setLoading(true);
        fetch(`${API_BASE}/gsc_get_status.php?days=${days}`, { headers: { "Authorization": `Bearer ${token}` } })
            .then(r => r.json())
            .then(j => { if (alive) { setData(j); setLoading(false); } })
            .catch(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [days, token]);

    // CSV-Export für Claude Desktop (gsc_export_csv.php, grain status|queries) via Blob+Bearer.
    const downloadCsv = async (grain) => {
        try {
            const res = await fetch(`${API_BASE}/gsc_export_csv.php?grain=${grain}`, { headers: { "Authorization": `Bearer ${token}` } });
            if (!res.ok) { console.error("CSV HTTP", res.status); return; }
            const blob = await res.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `gsc_${grain}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
        } catch (e) { console.error("CSV-Export-Fehler:", e); }
    };

    // Jede Spalte sortierbar. Klick zyklisch: Vorgabe → 1. Richtung → Gegenrichtung
    // → Vorgabe. Die Vorgabe (Probleme zuerst, dann Impressionen) kommt vom
    // Endpunkt und bleibt erreichbar — sie ist die Arbeitsreihenfolge.
    // `erst` = die Richtung, die beim ersten Klick sinnvoll ist: bei Zahlen die
    // große zuerst, bei Crawl und Rank die schlechte zuerst (älteste / hinterste).
    const shown = useMemo(() => {
        const liste = (data?.pages || []).filter(p => !onlyProblems || p.problem_level !== 'ok');
        if (!sort) return liste;
        const wert = SPALTEN[sort.key]?.wert;
        if (!wert) return liste;
        const vz = sort.dir === 'asc' ? 1 : -1;
        return [...liste].sort((a, b) => {
            const x = wert(a), y = wert(b);
            if (typeof x === 'string' || typeof y === 'string') {
                return vz * String(x).localeCompare(String(y), 'de');
            }
            return vz * (x - y);
        });
    }, [data, onlyProblems, sort]);

    const klickSpalte = key => {
        const erst = SPALTEN[key].erst;
        setSort(s0 => !s0 || s0.key !== key ? { key, dir: erst }
                    : s0.dir === erst ? { key, dir: erst === 'asc' ? 'desc' : 'asc' }
                    : null);
    };
    // Tooltip bewusst KURZ (eine Zeile): ein langer title-Kasten legt sich über
    // mehrere Tabellenzeilen und macht sie unlesbar (Georg 2026-08-22).
    // Die ausführliche Erklärung steht in der Fußzeile, nicht im Tooltip.
    const Th = ({ id, title, children }) => (
        <th className="gs-th-sort" onClick={() => klickSpalte(id)} title={title}>
            {children}{' '}
            <span className="gs-sort-pfeil">
                {sort?.key !== id ? '⇅' : sort.dir === 'asc' ? '▲' : '▼'}
            </span>
        </th>
    );

    const s = data?.summary || {};

    return (
        <div className="gs-wrap">
            <Link to="/admin" className="gs-back">← Zurück zum Dashboard</Link>
            <h2>🔎 GSC Seiten-Status <small>— wo steht jede LP/LB, Rank &amp; Probleme</small></h2>

            {loading ? <p>lädt…</p> : !data?.success ? <p>Fehler beim Laden.</p> : (
            <>
                <div className="gs-summary">
                    <span className="gs-pill">{s.total} Seiten</span>
                    <span className="gs-pill gs-b-ok">✅ {s.indexed} indexiert</span>
                    <span className="gs-pill gs-b-crit">🔴 {s.critical} kritisch</span>
                    <span className="gs-pill gs-b-warn">🟡 {s.warn} Warnung</span>
                    <span className="gs-pill gs-note">Rank = Ø-Position der letzten {s.window_days} Tage (bis {s.latest_day}) · niedriger = besser</span>
                </div>

                <div className="gs-controls">
                    <label><input type="checkbox" checked={onlyProblems} onChange={e => setOnlyProblems(e.target.checked)} /> nur Probleme zeigen</label>
                    <span className="gs-days">Fenster:&nbsp;
                        {[7, 14, 28].map(d => (
                            <button key={d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>{d}T</button>
                        ))}
                    </span>
                    <span className="gs-count">
                        {shown.length} angezeigt
                        {sort && (
                            <button className="gs-sort-reset" onClick={() => setSort(null)}
                                title="Zurück zur Vorgabe: Probleme zuerst">
                                sortiert: {SPALTEN[sort.key].label} {sort.dir === 'asc' ? '▲' : '▼'} ✕
                            </button>
                        )}
                    </span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button onClick={() => downloadCsv('status')} title="Status-CSV: 1 Zeile je Seite (Index-Status + Problem-Gruppe + Performance). Für Excel/Claude Desktop."
                                style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #1a7f5a', background: '#eafaf3', color: '#1a7f5a', cursor: 'pointer', fontSize: '13px' }}>⬇ Status-CSV</button>
                        <button onClick={() => downloadCsv('queries')} title="Queries-CSV: je (Seite × Suchbegriff) — für Content-/Kannibalisierungs-Analyse."
                                style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #1a5a7f', background: '#eaf3fa', color: '#1a5a7f', cursor: 'pointer', fontSize: '13px' }}>⬇ Queries-CSV</button>
                    </span>
                </div>

                <div className="gs-tablewrap">
                <table className="gs-table">
                    <thead><tr>
                        <Th id="prefix" title="Sortieren: LP-/LB-Kennung">Seite</Th>
                        <Th id="spark"  title="Sortieren: Impressionen der letzten 30 Tage">Impr. 30T</Th>
                        <Th id="pillar" title="Sortieren: Themen-Säule">Pillar</Th>
                        <Th id="index"  title="Sortieren: nicht indexiert zuerst">Index</Th>
                        <Th id="crawl"  title="Sortieren: ältester Crawl zuerst">🕷 Crawl</Th>
                        <Th id="rank"   title="Sortieren: hinterste Position zuerst">Rank</Th>
                        <Th id="impr"   title="Sortieren: meiste Impressionen zuerst">Impr</Th>
                        <Th id="klicks" title="Sortieren: meiste Klicks zuerst">Klicks</Th>
                        <Th id="notes"  title="Sortieren: meiste Notizen zuerst">📝 Notizen <small style={{ fontWeight: 400, color: '#94a3b8' }}>(Problemtext vorbefüllt)</small></Th>
                    </tr></thead>
                    <tbody>
                        {shown.map(p => (
                            <tr key={p.prefix + p.url} className={`gs-row gs-row-${p.problem_level}`}>
                                <td className="gs-seite">
                                    <strong>{p.prefix}</strong>
                                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="gs-link" title={p.url}>↗</a>
                                </td>
                                <td className="gs-spark-cell"><Sparkline values={p.spark} /></td>
                                <td>{p.pillar ? <span className="gs-pillar" style={{ background: PILLAR_COLORS[p.pillar] || '#888' }}>{p.pillar}</span> : ''}</td>
                                <td><IndexBadge p={p} /></td>
                                <td className="gs-crawl-cell"><CrawlHistory page={p} /></td>
                                <td className="gs-rank">{p.rank !== null ? String(p.rank).replace('.', ',') : '–'}</td>
                                <td>{p.impressions}</td>
                                <td>{p.clicks}</td>
                                <td className="gs-notes-cell"><NotesLog page={p} token={token} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                <p className="gs-foot">Datenquellen: <code>gsc_url_inspection</code> (Index/Crawl, tägl. 04:10) + <code>gsc_crawl_history</code> (Crawl-Verlauf, direkt nach dem Inspect — jeder neue Zeitpunkt, nichts wird überschrieben) + <code>gsc_daily_data</code> (Rank/Impr, tägl. 04:00). <b>🕷 Crawl</b> = letzter Googlebot-Besuch, Zahl dahinter = Alter in Tagen, <code>n×</code> = erfasste Zeitpunkte; klicken öffnet den Verlauf. <b>🔗n</b> = verweisende Seiten laut Google zum Crawl-Zeitpunkt (<b>rot bei 0</b> — auf diese Seite zeigt kein bekannter Link); Google liefert dazu eine <b>Stichprobe</b>, keine vollständige Liste. <b>Google kennt kein „Indexierungsdatum"</b> — die Crawl-Zeit ist die einzige echte Zeitangabe je URL. Der Verlauf sammelt ab 2026-08-22, rückwirkend gibt es ihn nicht. <b>„Impr. 30T"</b> = Impressionen je Tag der rollierenden letzten 30 Tage (grün); „–" = keine Impressionen (bei dem Volumen normal). Rank-Deltas &amp; „0 Impr." sind bei dem Volumen bewusst KEINE Flags (Rauschen) — belastbar sind Index-/Crawl-Signale.</p>
            </>
            )}
        </div>
    );
}
